import React from 'react';
import { Phone, MessageCircle, AlertTriangle, Clock, Shield, Heart, ExternalLink } from 'lucide-react';
import AppHeader from '../components/AppHeader';

export default function TeleManasInfo() {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AppHeader title="Tele-MANAS" />

      <div className="container mx-auto max-w-3xl px-4 py-6 space-y-6">
        {/* Emergency Alert */}
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6" data-testid="emergency-alert">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-red-800 mb-2">
                If you're in crisis
              </h2>
              <p className="text-red-700 mb-4">
                If you or someone you know is in immediate danger, please call emergency services (112) 
                or reach out to the Tele-MANAS helpline immediately.
              </p>
              <a 
                href="tel:14416"
                className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 transition-colors"
                data-testid="call-telemanas-button"
              >
                <Phone size={20} />
                Call 14416 Now
              </a>
            </div>
          </div>
        </div>

        {/* About Tele-MANAS */}
        <div className="card p-6" data-testid="about-telemanas">
          <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4 flex items-center gap-2">
            <Heart size={20} className="text-primary" />
            What is Tele-MANAS?
          </h3>
          <p className="text-[#718096] mb-4">
            Tele-MANAS (Tele Mental Health Assistance and Networking Across States) is a free, 
            government-run mental health helpline available 24/7. It provides accessible mental 
            health support to all citizens of India.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-[#FAF9F6] rounded-xl">
              <Phone size={20} className="text-primary" />
              <div>
                <div className="font-medium text-[#2D3748]">Toll-Free Number</div>
                <div className="text-[#718096]">14416 or 1800-891-4416</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#FAF9F6] rounded-xl">
              <Clock size={20} className="text-primary" />
              <div>
                <div className="font-medium text-[#2D3748]">Available</div>
                <div className="text-[#718096]">24 hours, 7 days a week</div>
              </div>
            </div>
          </div>
        </div>

        {/* Services Offered */}
        <div className="card p-6" data-testid="services-section">
          <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4 flex items-center gap-2">
            <Shield size={20} className="text-primary" />
            Services Offered
          </h3>
          <div className="space-y-3">
            {[
              { title: 'First-Level Support', desc: 'Trained counselors available for immediate emotional support' },
              { title: 'Specialist Consultation', desc: 'Access to psychiatrists and clinical psychologists' },
              { title: 'Multi-Language Support', desc: 'Services available in multiple Indian languages' },
              { title: 'Follow-up Care', desc: 'Continuous support and follow-up for ongoing care' },
              { title: 'Referral Services', desc: 'Connection to nearby mental health facilities' }
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-[#FAF9F6] rounded-xl">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary text-sm font-medium">{index + 1}</span>
                </div>
                <div>
                  <div className="font-medium text-[#2D3748]">{service.title}</div>
                  <div className="text-sm text-[#718096]">{service.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* When to Call */}
        <div className="card p-6" data-testid="when-to-call">
          <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4 flex items-center gap-2">
            <MessageCircle size={20} className="text-primary" />
            When Should You Call?
          </h3>
          <ul className="space-y-2 text-[#718096]">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              Feeling overwhelmed, anxious, or depressed
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              Experiencing thoughts of self-harm or suicide
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              Coping with grief, loss, or trauma
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              Struggling with addiction or substance use
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              Need someone to talk to about mental health concerns
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              Seeking professional mental health advice
            </li>
          </ul>
        </div>

        {/* Official Links */}
        <div className="card p-6" data-testid="official-links">
          <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4">
            Official Resources
          </h3>
          <div className="space-y-3">
            <a 
              href="https://telemanas.mohfw.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-primary/50 hover:bg-primary/5 transition-all"
              data-testid="telemanas-website-link"
            >
              <div>
                <div className="font-medium text-[#2D3748]">Tele-MANAS Official Website</div>
                <div className="text-sm text-[#718096]">telemanas.mohfw.gov.in</div>
              </div>
              <ExternalLink size={20} className="text-primary" />
            </a>
            <a 
              href="https://nimhans.ac.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-primary/50 hover:bg-primary/5 transition-all"
              data-testid="nimhans-website-link"
            >
              <div>
                <div className="font-medium text-[#2D3748]">NIMHANS</div>
                <div className="text-sm text-[#718096]">National Institute of Mental Health and Neurosciences</div>
              </div>
              <ExternalLink size={20} className="text-primary" />
            </a>
          </div>
        </div>

        {/* Privacy Note */}
        <div className="text-center text-sm text-[#A0AEC0] py-4">
          <p>Your privacy and confidentiality are protected</p>
          <p className="mt-1">All Tele-MANAS services are free of cost</p>
        </div>
      </div>
    </div>
  );
}
